import {
  type Allowance,
  type Discount,
  type Product
} from "~/api/products/types";

import { type KioskSession } from "../../types";

type CalculateAllowanceArgs = {
  allowances: Allowance[];
  discounts: Discount[];
  products: Product[];
  basket: KioskSession["basket"];
};

export function calculateAllowance({
  allowances,
  discounts,
  basket,
  products
}: CalculateAllowanceArgs): Allowance[] {
  if (!allowances.length) return [];

  return allowances.map((allowance) => {
    const discount = discounts.find((d) => d.id === allowance.discountId);

    if (!discount) return allowance;

    const productIds = products
      .filter((product) => product.discount?.id === discount.id)
      .map((product) => product.id);

    if (!productIds.length) return allowance;

    const quantityInBasket = basket.reduce((acc, item) => {
      if (productIds.includes(item.productId)) {
        return acc + item.quantity;
      }

      return acc;
    }, 0);

    if (!quantityInBasket) return allowance;

    let remaining = 0;

    if (allowance.type === "unitPrice") {
      const appliedUnitPriceDiscount = basket.reduce((acc, item) => {
        if (productIds.includes(item.productId)) {
          const product = products.find((p) => p.id === item.productId);
          if (!product) return acc;

          if (discount.amountType === "currency") return acc;

          const unitDiscountedPrice =
            (product.unitPrice * discount.amount) / 100;
          return acc + unitDiscountedPrice * item.quantity;
        }

        return acc;
      }, 0);

      remaining = allowance.remaining - appliedUnitPriceDiscount;
    }

    if (allowance.type === "numberOfProducts") {
      remaining = allowance.remaining - quantityInBasket;
    }

    return {
      ...allowance,
      isAllowanceOver: remaining <= 0,
      remaining: Math.max(0, remaining)
    };
  });
}
